// Brand page inventory: CaboLab is exposed publicly at `/apotheke` via this route file (`_public.apotheke.tsx`).
import { Link, Outlet, useLocation, useMatches } from "react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Banner } from "~/features/core/Banner";
import { NavBar } from "~/features/public/NavBar";
import CaboLabLogoPng from "~/assets/logos/new_logos/CaboLab Claim@4x.png";

export default function ApothekeIndexPage() {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState("start");

  const location = useLocation();
  const path = location.pathname.split("#")[0];

  useEffect(() => {
    if (path?.endsWith("products")) {
      setActiveSection("products");
    }
  }, [path]);

  const sections = useMemo(
    () => [
      { id: "start", path: "/apotheke#start", hash: "start", label: t("Start") },
      { id: "benefits", path: "/apotheke#benefits", hash: "benefits", label: t("Vorteile") },
      { id: "services", path: "/apotheke#services", hash: "services", label: t("Services") },
      { id: "products", path: "/apotheke/products", hash: "products", label: t("Angebot") },
      { id: "trainings", path: "/apotheke#trainings", hash: "trainings", label: t("Schulungen") },
      { id: "contact", path: "/apotheke#contact", hash: "contact", label: t("Kontakt aufnehmen") },
      { id: "faq", path: "/apotheke#faq", hash: "faq", label: t("FAQ") },
    ],
    [t],
  );

  useEffect(() => {
    const handleScroll = () => {
      const currentSection = sections.find((section) => {
        const element = document.getElementById(section.id);
        if (element) {
          const rect = element.getBoundingClientRect();

          return rect.top <= 110 && rect.bottom >= 100;
        }
        return false;
      });
      if (currentSection) {
        setActiveSection((previous_active_section_id: string) => {
          const current_section_hash = currentSection.hash;
          const previous_section = sections.find((section) => section.id === previous_active_section_id);
          const previous_section_hash = previous_section?.hash;
          const hash = currentSection.hash;
          // Change the URL hash without scrolling
          if (previous_section_hash !== current_section_hash) {
            window.history.replaceState(null, "", `/apotheke#${hash}`);
          }
          return currentSection.id;
        });
      }
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname, sections]);

  const matches = useMatches();
  const isLoggedIn: boolean = (matches[0]?.data as any)?.loggedIn ?? false;

  const right = (
    <Link
      className="inline-flex items-center justify-center rounded-xl bg-blue-550 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-800/20 transition hover:-translate-y-0.5 hover:bg-blue-900"
      to={isLoggedIn ? "/client/dashboard" : "/v1/auth/login"}
    >
      {t("Login")}
    </Link>
  );

  return (
    <div className="relative font-open-sans antialiased">
      <Banner />
      <NavBar
        activeSection={activeSection}
        logo={<img src={CaboLabLogoPng} alt="CaboLab" className="h-20" />}
        logoTo="/"
        navItems={[
          { to: `/apotheke#start`, label: t("Start"), id: "start" },
          { to: `/apotheke#benefits`, label: t("Vorteile"), id: "benefits" },
          { to: `/apotheke#services`, label: t("Services"), id: "services" },
          { to: `/apotheke/products`, label: t("Angebot"), id: "products" },
          { to: `/apotheke#trainings`, label: t("Schulungen"), id: "trainings" },
          { to: `/apotheke#contact`, label: t("Kontakt aufnehmen"), id: "contact" },
          { to: `/apotheke#faq`, label: t("FAQ"), id: "faq" },
        ]}
        right={right}
        sticky
      />
      <Outlet />
    </div>
  );
}
